import { Checkbox, Input, MessageBarBody, MessageBarTitle, Textarea } from "@fluentui/react-components";
import { useEffect, useState } from "react";
import { connect } from "react-redux";

// components functions
import * as ComponentFunctions from './Addendum.Form.Functions';

// components
import { DateTimeInput } from "../../../../commons/component/Inputs/DateTimeInput/DateTimeInput";
import { NewContractAddendum } from "../../../../models/Entities/Contracts/NewContractAddendum";
import { ContractAddendum } from "../../../../models/Entities/Contracts/ContractAddendum";
import { GuideDrawer, GuideDrawerForm } from "../../../../theme/components/GuideDrawer";
import { GuideField, GuideFieldGrid, GuideHelpText } from "../../../../theme/components/GuideField";
import { GuideMessageBar } from "../../../../theme/components/GuideMessageBar";

interface FormValidation {
    success: boolean;
    errors: string[];
}

const AddendumFormComponent = (props: ComponentFunctions.Props) => {

    // form status
    const [submittedForm, setSubmittedForm] = useState<boolean>(false);
    const [FormValidationStatus, setFormValidationStatus] = useState<
        FormValidation | undefined
    >();

    // form fields
    const [AddendumName, setAddendumName] = useState<string | undefined>();
    const [AddendumDescription, setAddendumDescription] = useState<string | undefined>();
    const [AddendumStartDate, setAddendumStartDate] = useState<Date | undefined>();
    const [AddendumEndDate, setAddendumEndDate] = useState<Date | undefined>();
    // Estado de la adenda: la edición lo conserva salvo que se cambie este check (una adenda nueva nace habilitada).
    const [AddendumEnabled, setAddendumEnabled] = useState<boolean>(true);

    useEffect(() => {
        setAddendumName(props.CurrentAddendum?.contractAddendumName);
        setAddendumDescription(props.CurrentAddendum?.contractAddendumDescription);
        setAddendumStartDate(props.CurrentAddendum?.contractAddendumExpiryStartDate);
        setAddendumEndDate(props.CurrentAddendum?.contractAddendumExpiryEndDate);
        setAddendumEnabled(props.CurrentAddendum?.isEnable ?? true);
    }, [props.isOpen === true, props.CurrentAddendum]);

    useEffect(() => {
        if (props.isUpdatedSuccessfully || props.isCreatingAddendumSuccessfully) {
            ResetForm();
            props.onClose();
        }
    }, [props.isUpdatedSuccessfully, props.isCreatingAddendumSuccessfully]);


    const HandleCancelForm = () => {
        ResetForm();
        props.onClose();
    };

    const ValidateForm = (): boolean => {

        let validationResult: boolean = true;

        let FormValidation: FormValidation = { success: true, errors: [] };
        setFormValidationStatus(FormValidation);

        if (!AddendumName || AddendumName === '') {
            FormValidation.errors.push("Debe informar un nombre de adenda");
            validationResult = false;
        }

        FormValidation.success = validationResult;
        setFormValidationStatus(FormValidation);
        return validationResult;
    };
    
    const HandleSubmitForm = () => {

        setSubmittedForm(true);

        if (ValidateForm()) {
            // submit form
            if (!props.CurrentAddendum) {
                const newAddendum: NewContractAddendum = { 
                    contractId: props.ContractId,
                    contractAddendumName: AddendumName || '',
                    contractAddendumDescription: AddendumDescription || '',
                    contractAddendumExpiryStartDate: AddendumStartDate,
                    contractAddendumExpiryEndDate: AddendumEndDate,
                    contractAddendumType: 0                  
                };
                props.CreateContractAddendum(props.ContractId, newAddendum)
            } else {
                const updatedAddendum: ContractAddendum = {
                    id: props.CurrentAddendum.id,
                    contractId: props.ContractId,
                    contractAddendumName: AddendumName || '',
                    contractAddendumDescription: AddendumDescription || '',
                    contractAddendumExpiryStartDate: AddendumStartDate,
                    contractAddendumExpiryEndDate: AddendumEndDate,
                    contractAddendumType: props.CurrentAddendum.contractAddendumType,
                    isActive: false,
                    isEnable: AddendumEnabled
                };
                props.UpdateContractAddendum(props.ContractId, updatedAddendum);
            }
        } else {
            setSubmittedForm(false);
        }
    };

    // reset form
    const ResetForm = () => {
        setSubmittedForm(false);
        setFormValidationStatus(undefined);
        setAddendumName(undefined);
        setAddendumDescription(undefined);
        setAddendumStartDate(undefined);
        setAddendumEndDate(undefined);
        setAddendumEnabled(true);
    };

    const saveError = props.errorOnCreatingAddendum || props.errorOnUpdating;

    const FormFields = (
        <GuideDrawerForm onSubmit={HandleSubmitForm} testId="addendum-form">
            <GuideField
                label={"Nombre de adenda"}
                required
                error={FormValidationStatus?.success === false && !AddendumName ? "Debe informar un nombre de adenda" : undefined}
            >
                <Input
                    type="text"
                    id={'txt_addendumname'}
                    value={AddendumName ?? ''}
                    required={true}
                    maxLength={200}
                    onChange={(_, data) => setAddendumName(data.value)}
                    onFocus={(e) => e.currentTarget.select()}
                />
            </GuideField>
            <GuideField label={"Descripcion"}>
                <Textarea
                    id={'txt_addendumdescription'}
                    value={AddendumDescription ?? ''}
                    resize="vertical"
                    onChange={(_, data) => setAddendumDescription(data.value)}
                    onFocus={(e) => e.currentTarget.select()}
                />
            </GuideField>
            <GuideFieldGrid>
                <DateTimeInput label={"Vigencia desde"} placeHolder={"dd/mm/yyyy"} selectedDate={AddendumStartDate ? new Date(AddendumStartDate) : undefined} onSelectDate={(date: Date | undefined) => setAddendumStartDate(date) } isSubmitted={submittedForm} isRequired={false} errorMessage={undefined} />
                <DateTimeInput label={"Vigencia hasta"} placeHolder={"dd/mm/yyy"} selectedDate={AddendumEndDate ? new Date(AddendumEndDate): undefined} onSelectDate={(date: Date | undefined) => setAddendumEndDate(date) } isSubmitted={submittedForm} isRequired={false} errorMessage={undefined} />
            </GuideFieldGrid>
            {props.CurrentAddendum && (
                <div>
                    <Checkbox
                        id={'chk_addendumenabled'}
                        label={"Habilitada"}
                        checked={AddendumEnabled}
                        onChange={(_, data) => setAddendumEnabled(!!data.checked)}
                    />
                    <GuideHelpText>Una adenda deshabilitada queda como referencia, sin uso operativo.</GuideHelpText>
                </div>
            )}
        </GuideDrawerForm>
    );

    return (
        <GuideDrawer
            open={props.isOpen}
            size="medium"
            eyebrow={props.Contract?.contractNumber || undefined}
            title="Información de Adenda"
            onDismiss={HandleCancelForm}
            testId="addendum-drawer"
            secondaryAction={{ label: 'Cancelar', onClick: HandleCancelForm, disabled: submittedForm }}
            primaryAction={{
                label: 'Guardar',
                onClick: HandleSubmitForm,
                disabled: submittedForm,
                loading: props.isCreatingAddendum || props.isUpdating
            }}
        >
            {FormValidationStatus?.success === false && (
                <GuideMessageBar intent="error" layout="multiline">
                    <MessageBarBody>
                        <MessageBarTitle>Verifique la información suministrada</MessageBarTitle>
                        <ul>
                            {FormValidationStatus.errors.map((error: string, index: number) => (
                                <li key={index}>{error}</li>
                            ))}
                        </ul>
                    </MessageBarBody>
                </GuideMessageBar>
            )}
            {(props.failOnCreatingAddendum === true || props.failOnUpdating) && (
                <GuideMessageBar intent="error" layout="multiline">
                    <MessageBarBody>
                        <MessageBarTitle>{saveError?.ErrorMessage || 'Ocurrio un error inesperado'}</MessageBarTitle>
                        {saveError?.Errors && saveError.Errors.length > 0 && (
                            <ul>
                                {saveError.Errors.map((error: string, index: number) => (
                                    <li key={index}>{error}</li>
                                ))}
                            </ul>
                        )}
                    </MessageBarBody>
                </GuideMessageBar>
            )}
            {props.AddendumId ? props.CurrentAddendum && FormFields : FormFields}
        </GuideDrawer>
    );

};

export { AddendumFormComponent };

export default connect(
    ComponentFunctions.mapStateToProps,
    ComponentFunctions.mapDispatchToProps
)(AddendumFormComponent as any);
